import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { Row, Space, Input, Col, UploadFile, Empty, Pagination } from 'antd';
import SportsCard from '../components/features/Sports/SportsCard';
import Button from '../components/common/Button';
import DeleteModal from '../components/shared/DeleteModal';
import { Sport, SportsCreationType, SportSearchParams } from '../types/types';
import { useSnackbar } from '../contexts/SnackbarContext/SnackbarProvider';
import { COLORS, TEXT_SIZES } from '../utils/constants';
import {
  getAllSportsRequest,
  createSportRequest,
  updateSportRequest,
  deleteSportRequest,
} from '../api/requests/sportsmanagement';
import { PlusCircleOutlined } from '@ant-design/icons';
import SportsModal from '../components/features/Sports/SportsModal';
import {
  deleteAttachmentRequest,
  uploadFileRequest,
} from '../api/requests/attachments';
import { EntityType } from '../enums/enums';
import { StyledLoadingWrapper } from '../components/common/CustomTable';
import Spinner from '../components/common/Spinner';
import { debounce } from '../utils/utils';

const { Search } = Input;

export type SportsCreationWithImage = SportsCreationType & {
  uploadSportImage?: UploadFile[];
};

const SportsManagement: React.FC = () => {
  const [query, setQuery] = useState<string>('');
  const [inputValue, setInputValue] = useState<string>('');
  const [sports, setSports] = useState<Sport[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [sportToDelete, setSportToDelete] = useState<Sport | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false);
  const [sportToEdit, setSportToEdit] = useState<Sport | null>(null);
  const snackbar = useSnackbar();
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const debounceQuery = useMemo(
    () =>
      debounce((query) => {
        setQuery(query);
        setCurrentPage(1);
      }, 500),
    [],
  );
  const pageSize = 9;

  const fetchAllSports = async () => {
    try {
      setLoading(true);
      const params: SportSearchParams = {
        pageNumber: Math.max(0, currentPage - 1),
        pageSize: pageSize,
        searchQuery: query || undefined,
      };
      const response = await getAllSportsRequest(params);
      if (response) {
        setSports(response?.value?.rows || []);
        setTotalCount(response?.value?.totalCount || 0);
      }
    } catch (error) {
      console.error('Error fetching sports:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllSports();
  }, [currentPage, query]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    debounceQuery(value);
  };

  const handleEdit = (sport: Sport) => {
    setSportToEdit(sport);
    setIsAddModalOpen(true);
  };

  const handleDelete = (sport: Sport) => {
    setSportToDelete(sport);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
    setSportToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!sportToDelete) return;

    try {
      setLoading(true);
      const response = await deleteSportRequest(sportToDelete.sportId);
      if (response?.isSuccess) {
        setSports((prevSports) =>
          prevSports.filter((s) => s.sportId !== sportToDelete.sportId),
        );
        snackbar.success('Sport deleted successfully');
        setCurrentPage(1);
        setIsDeleteModalOpen(false);
        setSportToDelete(null);
        fetchAllSports();
      }
    } catch (error: any) {
      snackbar.error(error?.Error || 'Error deleting sport');
    } finally {
      setLoading(false);
    }
  };

  const handleAddNewSport = () => {
    setIsAddModalOpen(true);
  };

  const handleCancel = () => {
    setSportToEdit(null);
    setIsAddModalOpen(false);
  };

  const handleAddNewSportSubmit = async (values: SportsCreationWithImage) => {
    try {
      const response = await createSportRequest(values);
      if (response?.isSuccess && response?.value) {
        if (values?.uploadSportImage) {
          const file = values?.uploadSportImage[0]?.originFileObj;
          if (file) {
            try {
              const uploadResponse = await uploadFileRequest(
                file,
                EntityType.Sport,
                response.value,
                'Sport Image',
                true,
                1,
                true,
              );
              if (uploadResponse && uploadResponse.isFailure) {
                snackbar.error(
                  uploadResponse.error || 'Error while uploading image',
                );
              }
            } catch (error: any) {
              snackbar.error(error?.Error || 'Error while uploading image');
            }
          }
        }
        snackbar.success('Sport created successfully');
        setIsAddModalOpen(false);
        fetchAllSports();
      }
    } catch (error: any) {
      snackbar.error(error?.Error || 'Failed to create sport');
    }
  };

  const handleEditSportSubmit = async (values: SportsCreationWithImage) => {
    if (!sportToEdit) {
      snackbar.error('No sport selected for editing');
      return;
    }
    try {
      const updateReqBody = { ...sportToEdit, ...values };
      const response = await updateSportRequest(
        sportToEdit?.sportId,
        updateReqBody,
      );
      if (response?.isSuccess && response?.value) {
        const newFile = values?.uploadSportImage?.[0]?.originFileObj;
        const oldAttachmentId = sportToEdit?.attachmentUrlDto?.attachmentId;
        if (newFile) {
          try {
            if (oldAttachmentId) {
              await deleteAttachmentRequest(oldAttachmentId);
            }
            const uploadResponse = await uploadFileRequest(
              newFile,
              EntityType.Sport,
              response.value,
              'Sport Image',
              true,
              1,
              true,
            );
            if (uploadResponse && uploadResponse.isFailure) {
              snackbar.error(
                uploadResponse.error || 'Error while uploading image',
              );
            }
          } catch (error: any) {
            snackbar.error(error?.Error || 'Error while updating sport image');
          }
        }
        snackbar.success('Sport updated successfully');
        setIsAddModalOpen(false);
        fetchAllSports();
        setSportToEdit(null);
      }
    } catch (error: any) {
      snackbar.error(error?.Error || 'Failed to update sport');
    }
  };

  return (
    <Container>
      <Header>
        <StyledSpace direction="horizontal">
          <StyledSearch
            placeholder="Search by sports, description..."
            onChange={handleSearchChange}
            enterButton
            size="large"
            allowClear
            loading={loading}
            value={inputValue}
          />
          <Button
            type="primary"
            icon={<PlusCircleOutlined style={{ fontSize: TEXT_SIZES.LG }} />}
            onClick={handleAddNewSport}
            width="182px"
            disabled={loading}
          >
            Add New Sport
          </Button>
        </StyledSpace>
      </Header>

      {loading ? (
        <StyledLoadingWrapper>
          <Spinner />
        </StyledLoadingWrapper>
      ) : (
        <StyledSpace direction="vertical" size="middle">
          <Row gutter={[16, 16]}>
            {sports.length > 0
              ? sports.map((sport) => (
                  <Col key={sport.sportId} xs={24} sm={12} md={8} lg={8}>
                    <SportsCard
                      sport={sport}
                      onDelete={handleDelete}
                      onEdit={handleEdit}
                    />
                  </Col>
                ))
              : !loading && (
                  <StyledEmpty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                )}
          </Row>
          {totalCount > 0 && (
            <Pagination
              align="end"
              current={currentPage}
              pageSize={pageSize}
              total={totalCount}
              onChange={setCurrentPage}
            />
          )}
        </StyledSpace>
      )}
      <SportsModal
        // Remount per selected sport (or "new") so no internal state — including the
        // uploaded image preview — leaks from a previously edited sport.
        key={sportToEdit?.sportId ?? 'new-sport'}
        visible={isAddModalOpen}
        onClose={handleCancel}
        onSubmit={sportToEdit ? handleEditSportSubmit : handleAddNewSportSubmit}
        initialValues={sportToEdit}
      />
      <DeleteModal
        open={isDeleteModalOpen}
        moduleType="Sport"
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
      />
    </Container>
  );
};

const Container = styled.div`
  padding: 8px;
  background: ${COLORS.white.primary};
  min-height: 100vh;
`;

const Header = styled(Row)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const StyledSpace = styled(Space)`
  width: 100%;
  & .ant-space-item:first-child {
    flex: 1;
  }
`;

const StyledSearch = styled(Search)`
  flex: 1;
  .ant-btn-variant-solid {
    background-color: ${COLORS.blue.primary};
  }
`;

const StyledEmpty = styled(Empty)`
  flex: 1;
  width: 100%;
  text-align: center;
  justify-content: center;
`;

export default SportsManagement;
